import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DocumentsTab = ({ documents, canUpload = false, canDownload = false }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { value: 'all', label: 'All Documents' },
    { value: 'contracts', label: 'Contracts' },
    { value: 'certifications', label: 'Certifications' },
    { value: 'identification', label: 'Identification' },
    { value: 'performance', label: 'Performance Reviews' },
    { value: 'training', label: 'Training Records' },
    { value: 'other', label: 'Other' }
  ];

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'FileText';
      case 'doc': case'docx':
        return 'FileText';
      case 'jpg': case'jpeg': case'png':
        return 'Image';
      case 'xls': case'xlsx':
        return 'FileSpreadsheet';
      default:
        return 'File';
    }
  };

  const getFileColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf':
        return 'var(--color-error)';
      case 'doc': case'docx':
        return 'var(--color-primary)';
      case 'jpg': case'jpeg': case'png':
        return 'var(--color-success)';
      case 'xls': case'xlsx':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'rejected':
        return 'text-error bg-error/10';
      case 'expired':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const filteredDocuments = documents?.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc?.category === selectedCategory;
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         doc?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleUpload = () => {
    setUploadModalOpen(true);
  };

  const handleDownload = (document) => {
    // Handle download logic here
    console.log('Downloading:', document?.name);
  };

  const handleView = (document) => {
    // Handle view logic here
    console.log('Viewing:', document?.name);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-semibold text-foreground">Documents & Files</h3>
        {canUpload && (
          <Button onClick={handleUpload}>
            <Icon name="Upload" size={16} className="mr-2" />
            Upload Document
          </Button>
        )}
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories?.map((category) => (
            <Button
              key={category?.value}
              variant={selectedCategory === category?.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category?.value)}
              className="whitespace-nowrap"
            >
              {category?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments?.map((document) => (
          <div key={document?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon 
                    name={getFileIcon(document?.type)} 
                    size={20} 
                    color={getFileColor(document?.type)} 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{document?.name}</h4>
                  <p className="text-sm text-muted-foreground">{document?.type?.toUpperCase()} • {document?.size}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(document?.status)}`}>
                {document?.status?.charAt(0)?.toUpperCase() + document?.status?.slice(1)}
              </span>
            </div>

            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{document?.description}</p>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Uploaded:</span>
                <span className="text-foreground">{document?.uploadDate}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Uploaded by:</span>
                <span className="text-foreground">{document?.uploadedBy}</span>
              </div>
              {document?.expiryDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Expires:</span>
                  <span className={`${new Date(document.expiryDate) < new Date() ? 'text-error' : 'text-foreground'}`}>
                    {document?.expiryDate}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleView(document)}
                className="flex-1"
              >
                <Icon name="Eye" size={14} className="mr-2" />
                View
              </Button>
              {canDownload && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(document)}
                  className="flex-1"
                >
                  <Icon name="Download" size={14} className="mr-2" />
                  Download
                </Button>
              )}
            </div>

            {document?.versions && document?.versions?.length > 1 && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="History" size={14} />
                  <span>{document?.versions?.length} versions available</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {filteredDocuments?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileX" size={24} color="var(--color-muted-foreground)" />
          </div>
          <h4 className="font-medium text-foreground mb-2">No documents found</h4>
          <p className="text-muted-foreground mb-4">
            {searchTerm || selectedCategory !== 'all' ?'Try adjusting your search or filter criteria' :'No documents have been uploaded yet'
            }
          </p>
          {canUpload && (
            <Button onClick={handleUpload}>
              <Icon name="Upload" size={16} className="mr-2" />
              Upload First Document
            </Button>
          )}
        </div>
      )}
      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1000 p-4">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-foreground">Upload Document</h4>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setUploadModalOpen(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <Input
                label="Document Name"
                placeholder="Enter document name"
              />
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Category</label>
                <select className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground">
                  {categories?.slice(1)?.map((category) => (
                    <option key={category?.value} value={category?.value}>
                      {category?.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <Input
                label="Description"
                placeholder="Brief description of the document"
              />
              
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Icon name="Upload" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop your file here, or click to browse
                </p>
                <Button variant="outline" size="sm">
                  Choose File
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setUploadModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button className="flex-1">
                Upload
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;